import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_ROOT } from "../service/Services";
import Spinner from "../components/Spinner";
import {
  FaEdit,
  FaTrash,
  FaUndo,
  FaInfoCircle,
  FaPlusCircle,
  FaFolderOpen,
  FaSpinner,
  FaBell,
} from "react-icons/fa";

export default function Leads() {
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [toast, setToast] = useState({ message: "", visible: false });

  // Filters
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("All"); // status filter from status boxes (Open/New/..). "All" means no status filter
  const [filterLeadType, setFilterLeadType] = useState("Active"); // Active / Inactive filter (top-right select)
  const [activeFilter, setActiveFilter] = useState("Total");

  const leadTypes = [
    "All",
    "Standard MS Projects 16-Hour Training",
    "Tableau End User 2-Day Training",
    "Power Automate 10-Hour Training",
    "MS PowerPoint 14-Hour Training",
    "MS Outlook Training",
    "MS Word 12-Hour Training",
    "AI with MS Office",
    "Excel Training",
    "Power BI Corporate Training",
    "Corporate Training",
  ];

  const [newLead, setNewLead] = useState({
    date: new Date().toISOString().split("T")[0],
    typeOfLead: "Corporate Training",
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    designation: "",
    status: "Open",
    remark: "",
    reminderDate: "",
    leadType: "Active",
  });

  const authHeaders = useMemo(
    () => ({
      headers: { authorization: `Bearer ${localStorage.getItem("token")}` },
    }),
    []
  );

  const loadInit = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_ROOT}/leads`, authHeaders);
      setLeads(res.data?.leads || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create Lead
  const createLead = async () => {
    if (!newLead.contactName.trim()) {
      setToast({ message: "Contact Name is required", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API_ROOT}/leads`, newLead, authHeaders);
      const savedLead = res.data?.lead || res.data?.data;
      setLeads((prev) => [...prev, savedLead]);

      setNewLead({
        date: new Date().toISOString().split("T")[0],
        typeOfLead: "Corporate Training",
        companyName: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        designation: "",
        status: "Open",
        remark: "",
        reminderDate: "",
        leadType: "Active",
      });

      setToast({ message: "Lead created successfully", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to create lead", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Toggle Active / Inactive
  const handleToggleStatus = async (id) => {
    const lead = leads.find((l) => l._id === id);
    if (!lead) return;

    const isInactive = lead.leadType === "Inactive";
    if (
      !window.confirm(
        `Mark this lead as ${isInactive ? "Active" : "Inactive"}?`
      )
    )
      return;

    try {
      setLoading(true);
      const updatedLead = {
        ...lead,
        leadType: isInactive ? "Active" : "Inactive",
      };
      await axios.put(`${API_ROOT}/leads/${id}`, updatedLead, authHeaders);

      setLeads((prev) => prev.map((l) => (l._id === id ? updatedLead : l)));

      setToast({
        message: `Lead marked as ${isInactive ? "Active" : "Inactive"}`,
        visible: true,
      });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to update lead type", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Delete row
  const handleDeleteRow = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      setLoading(true);
      setLeads((prev) => prev.filter((lead) => lead._id !== id));
      await axios.delete(`${API_ROOT}/leads/${id}`, authHeaders);

      setToast({ message: "Lead deleted successfully", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to delete lead", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Edit
  const handleEdit = (lead) => {
    if (lead.leadType !== "Active") return;
    setEditLead({ ...lead });
  };

  const saveEdit = async () => {
    if (!editLead) return;

    try {
      setLoading(true);
      const res = await axios.put(
        `${API_ROOT}/leads/${editLead._id}`,
        editLead,
        authHeaders
      );
      const updatedLead = {
        ...editLead,
        ...(res.data?.lead || res.data?.data),
      };

      setLeads((prev) =>
        prev.map((l) => (l._id === updatedLead._id ? updatedLead : l))
      );
      setEditLead(null);

      setToast({ message: "Lead updated successfully!", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to update lead", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // Show in dd/mm/yyyy (en-GB)
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB");
  };

  // Helper: parse YYYY-MM-DD (or ISO) into local midnight date object
  const parseDateOnly = (dateStr) => {
    if (!dateStr) return null;
    // dateStr may be '2025-11-20' or full ISO. Extract YYYY-MM-DD
    const datePart = dateStr.split("T")[0];
    const [y, m, d] = datePart.split("-");
    return new Date(Number(y), Number(m) - 1, Number(d));
  };

  // Today at local midnight
  const todayStart = (() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  })();

  // -----------------------------
  // STATUS COUNTS (including Reminder logic combining A+B+C)
  // Reminder count = leads with (status === 'Reminder') OR (reminderDate >= today)
  // -----------------------------
  const statusCounts = {
    Open: leads.filter((l) => l.status === "Open").length,
    New: leads.filter((l) => l.status === "New").length,
    "In Progress": leads.filter((l) => l.status === "In Progress").length,
    Reminder: leads.filter((l) => {
      const remDate = parseDateOnly(l.reminderDate);
      const reminderDateMatches = remDate && remDate >= todayStart;
      return l.status === "Reminder" || reminderDateMatches;
    }).length,
  };

  // -----------------------------
  // APPLY FILTER ON STATUS BOX CLICK
  // -----------------------------
  const applyFilter = (status) => {
    setActiveFilter(status);
    if (status === "Total") {
      setFilterStatus("All");
    } else {
      setFilterStatus(status);
    }
  };

  // -----------------------------
  // FINAL FILTERING OF LEADS
  // -----------------------------
  const filteredLeads = leads.filter((lead) => {
    // 1) match lead type (Active/Inactive)
    const matchLeadType =
      filterLeadType === "All" ? true : lead.leadType === filterLeadType;

    // 2) match free-text type search
    const matchType =
      filterType.trim() === ""
        ? true
        : (lead.typeOfLead || "")
            .toLowerCase()
            .includes(filterType.toLowerCase());

    // 3) match status boxes (Open/New/In Progress) OR special Reminder filter
    let matchStatus = true;
    if (activeFilter === "Total" || filterStatus === "All") {
      matchStatus = true;
    } else if (activeFilter === "Reminder" || filterStatus === "Reminder") {
      // Show leads where status === 'Reminder' OR reminderDate >= today
      const remDate = parseDateOnly(lead.reminderDate);
      const reminderDateMatches = remDate && remDate >= todayStart;
      matchStatus = lead.status === "Reminder" || reminderDateMatches;
    } else {
      // Normal status filtering (Open / New / In Progress)
      matchStatus = lead.status === filterStatus;
    }

    return matchLeadType && matchType && matchStatus;
  });

  return (
    <div className="flex flex-col gap-6 w-full relative">
      {loading && <Spinner />}

      {toast.visible && (
        <div className="fixed top-5 right-5 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 transition-all duration-300">
          {toast.message}
        </div>
      )}

      {/* ------------------------------------------ */}
      {/*   ⭐ STATUS FILTER BOXES SECTION            */}
      {/* ------------------------------------------ */}

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* ⭐ TOTAL LEADS */}
        <div
          className={`p-4 cursor-pointer shadow rounded-lg text-center border ${
            activeFilter === "Total"
              ? "bg-teal-400 text-white"
              : "bg-teal-200 text-gray-700"
          }`}
          onClick={() => applyFilter("Total")}
        >
          <FaPlusCircle className="text-2xl mx-auto mb-2" />
          <h4>Total Leads</h4>
          <p className="text-2xl font-bold">
            {statusCounts.Open +
              statusCounts.New +
              statusCounts["In Progress"] 
              }
              {/* statusCounts.Reminder */}
          </p>
        </div>

        {/* ⭐ OPEN */}
        <div
          className={`p-4 cursor-pointer shadow rounded-lg text-center border ${
            activeFilter === "Open"
              ? "bg-green-300 text-green-900"
              : "bg-green-100 text-green-700"
          }`}
          onClick={() => applyFilter("Open")}
        >
          <FaFolderOpen className="text-2xl mx-auto mb-2" />
          <h4>Open</h4>
          <p className="text-2xl font-bold">{statusCounts.Open}</p>
        </div>

        {/* ⭐ NEW */}
        <div
          className={`p-4 cursor-pointer shadow rounded-lg text-center border ${
            activeFilter === "New"
              ? "bg-blue-300 text-blue-900"
              : "bg-blue-100 text-blue-700"
          }`}
          onClick={() => applyFilter("New")}
        >
          <FaPlusCircle className="text-2xl mx-auto mb-2" />
          <h4>New</h4>
          <p className="text-2xl font-bold">{statusCounts.New}</p>
        </div>

        {/* ⭐ IN PROGRESS */}
        <div
          className={`p-4 cursor-pointer shadow rounded-lg text-center border ${
            activeFilter === "In Progress"
              ? "bg-yellow-300 text-yellow-900"
              : "bg-yellow-100 text-yellow-700"
          }`}
          onClick={() => applyFilter("In Progress")}
        >
          <FaSpinner className="text-2xl mx-auto mb-2 animate-spin" />
          <h4>In Progress</h4>
          <p className="text-2xl font-bold">{statusCounts["In Progress"]}</p>
        </div>

        {/* ⭐ REMINDER */}
        <div
          className={`p-4 cursor-pointer shadow rounded-lg text-center border ${
            activeFilter === "Reminder"
              ? "bg-purple-300 text-purple-900"
              : "bg-purple-100 text-purple-700"
          }`}
          onClick={() => applyFilter("Reminder")}
        >
          <FaBell className="text-2xl mx-auto mb-2" />
          <h4>Reminder Date</h4>
          <p className="text-2xl font-bold">{statusCounts.Reminder}</p>
        </div>
      </div>

      {/* ------------------------------------------ */}
      {/*   ⭐ MAIN TABLE SECTION                    */}
      {/* ------------------------------------------ */}

      <div className="card p-4 shadow-md rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">All Leads</h3>
            <input
              type="text"
              placeholder="Search or type lead type..."
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border px-3 py-1 rounded-md w-60"
            />
          </div>
          <div className="flex">
            <select
              value={filterLeadType}
              onChange={(e) => setFilterLeadType(e.target.value)}
              className="border px-3 py-1 rounded-md w-40"
            >
              <option value="All">All Lead Type</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[1000px] overflow-y-auto max-h-[400px] relative">
            <table className="table-auto w-full border border-gray-200 text-sm">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="border px-2 py-1">Date</th>
                  <th className="border px-2 py-1">Type</th>
                  <th className="border px-2 py-1">Company</th>
                  <th className="border px-2 py-1">Name</th>
                  <th className="border px-2 py-1">Status</th>
                  <th className="border px-2 py-1">Remark</th>
                  <th className="border px-2 py-1">Reminder Date</th>
                  <th className="border px-2 py-1">Lead Type</th>
                  <th className="border px-2 py-1">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredLeads.length > 0 ? (
                  filteredLeads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50 relative">
                      <td className="border px-2 py-1">
                        {formatDate(lead.date)}
                      </td>
                      <td className="border px-2 py-1">{lead.typeOfLead}</td>
                      <td className="border px-2 py-1">{lead.companyName}</td>

                      <td className="border px-2 py-1 relative">
                        <div className="flex items-center gap-2">
                          <span>{lead.contactName}</span>
                          <div className="relative group">
                            <FaInfoCircle className="text-green-400 hover:text-green-600 cursor-pointer" />
                            <div className="absolute left-6 top-0 bg-white border border-gray-300 shadow-xl rounded-lg p-4 text-sm w-80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
                              <p>
                                <strong>Email:</strong> {lead.contactEmail}
                              </p>
                              <p>
                                <strong>Phone:</strong> {lead.contactPhone}
                              </p>
                              <p>
                                <strong>Designation:</strong> {lead.designation}
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="border px-2 py-1">{lead.status}</td>
                      <td className="border px-2 py-1">{lead.remark}</td>

                      <td className="border px-2 py-1">
                        {lead.reminderDate
                          ? formatDate(lead.reminderDate)
                          : "-"}
                      </td>

                      <td className="border px-2 py-1">
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-white text-sm font-medium mr-2 ${
                            lead.leadType === "Active"
                              ? "bg-green-400"
                              : "bg-red-400"
                          }`}
                        >
                          {lead.leadType || "Active"}
                        </span>

                        <button
                          onClick={() => handleToggleStatus(lead._id)}
                          className={`${
                            lead.leadType === "Active"
                              ? "text-blue-600 hover:text-green-800"
                              : "text-red-600 hover:text-red-800"
                          }`}
                          title="Toggle Active/Inactive"
                        >
                          <FaEdit />
                        </button>
                      </td>

                      <td className="border px-2 py-1 text-center flex gap-2 justify-center">
                        <button
                          onClick={() =>
                            lead.leadType === "Active" && handleEdit(lead)
                          }
                          disabled={lead.leadType !== "Active"}
                          className={`px-3 py-1 rounded border ${
                            lead.leadType === "Active"
                              ? "border-blue-600 text-blue-600 hover:bg-blue-100"
                              : "border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100"
                          }`}
                          title={
                            lead.leadType === "Active"
                              ? "Edit Lead"
                              : "Inactive lead cannot be edited"
                          }
                        >
                          Update
                        </button>

                        {/* <button
                          onClick={() => handleDeleteRow(lead._id)}
                          className="text-red-600 hover:text-black"
                          title="Delete Lead"
                        >
                          <FaTrash />
                        </button> */}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center p-2 text-gray-500">
                      No leads available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editLead && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-40">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Lead</h3>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                value={editLead.date?.split("T")[0]}
                onChange={(e) =>
                  setEditLead({ ...editLead, date: e.target.value })
                }
                className="border p-2 rounded w-full"
              />

              <input
                type="text"
                list="lead-types"
                value={editLead.typeOfLead}
                onChange={(e) =>
                  setEditLead({ ...editLead, typeOfLead: e.target.value })
                }
                placeholder="Type of Lead"
                className="border p-2 rounded w-full"
              />

              <datalist id="lead-types">
                {leadTypes
                  .filter((t) => t !== "All")
                  .map((type, idx) => (
                    <option key={idx} value={type} />
                  ))}
              </datalist>

              <input
                type="text"
                value={editLead.companyName}
                onChange={(e) =>
                  setEditLead({ ...editLead, companyName: e.target.value })
                }
                placeholder="Company Name"
                className="border p-2 rounded w-full col-span-2"
              />

              <input
                type="text"
                value={editLead.contactName}
                onChange={(e) =>
                  setEditLead({ ...editLead, contactName: e.target.value })
                }
                placeholder="Contact Name"
                className="border p-2 rounded w-full"
              />

              <input
                type="email"
                value={editLead.contactEmail}
                onChange={(e) =>
                  setEditLead({ ...editLead, contactEmail: e.target.value })
                }
                placeholder="Contact Email"
                className="border p-2 rounded w-full"
              />

              <input
                type="text"
                value={editLead.contactPhone}
                onChange={(e) =>
                  setEditLead({ ...editLead, contactPhone: e.target.value })
                }
                placeholder="Contact Phone"
                className="border p-2 rounded w-full"
              />

              <input
                type="text"
                value={editLead.designation}
                onChange={(e) =>
                  setEditLead({ ...editLead, designation: e.target.value })
                }
                placeholder="Designation"
                className="border p-2 rounded w-full"
              />

              <select
                value={editLead.status}
                onChange={(e) =>
                  setEditLead({ ...editLead, status: e.target.value })
                }
                className="border p-2 rounded w-full"
              >
                <option value="Open">Open</option>
                <option value="New">New</option>
                <option value="In Progress">In Progress</option>
                {/* <option value="Reminder">Reminder</option> */}
              </select>

              <textarea
                value={editLead.remark}
                onChange={(e) =>
                  setEditLead({ ...editLead, remark: e.target.value })
                }
                placeholder="Remark"
                className="border p-2 rounded w-full col-span-2"
              />

              <input
                type="date"
                value={editLead.reminderDate?.split("T")[0] || ""}
                onChange={(e) =>
                  setEditLead({ ...editLead, reminderDate: e.target.value })
                }
                className="border p-2 rounded w-full col-span-2"
              />
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditLead(null)}
                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition"
              >
                Cancel
              </button>

              <button
                onClick={saveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}